import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Home from './pages/Home'
import BlogPost from './pages/BlogPost'
import NotFound from './pages/NotFound'
import AdminDashboard from './pages/AdminDashboard'
import LoadingScreen from './components/LoadingScreen'
import { SocialLinksProvider } from './context/SocialLinksContext'
import { PortfolioProvider, usePortfolio } from './context/PortfolioContext'
import Chatbot from './components/Chatbot'

// Inner component that uses the portfolio context
function AppContent() {
  const { loading: portfolioLoading, portfolioData, error } = usePortfolio();
  const [showLoading, setShowLoading] = useState(true);
  const [loadingComplete, setLoadingComplete] = useState(false);

  useEffect(() => {
    // Ensure minimum 4 seconds of loading screen for animation
    const minLoadingTimer = setTimeout(() => {
      setLoadingComplete(true);
    }, 4000);

    return () => clearTimeout(minLoadingTimer);
  }, []);

  // Show loading screen if portfolio is loading OR minimum time hasn't passed
  const isReady = !portfolioLoading && portfolioData;
  const shouldShowLoading = !isReady || !loadingComplete;

  const handleLoadingComplete = () => {
    // Only hide loading screen if portfolio data is ready
    if (isReady) {
      setShowLoading(false);
    }
  };

  // Also hide loading screen when portfolio data becomes ready and minimum time has passed
  useEffect(() => {
    if (isReady && loadingComplete && showLoading) {
      const hideTimer = setTimeout(() => {
        setShowLoading(false);
      }, 500); // Small delay for smooth transition
      return () => clearTimeout(hideTimer);
    }
  }, [isReady, loadingComplete, showLoading]);

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-900">
        <div className="text-white text-center p-8">
          <h1 className="text-2xl font-bold mb-4">Application Error</h1>
          <p className="text-gray-400 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary text-white rounded hover:bg-opacity-90 transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <AnimatePresence>
        {showLoading && <LoadingScreen onComplete={handleLoadingComplete} />}
      </AnimatePresence>
      
      <AnimatePresence>
        {!showLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <Router>
              <div className="min-h-screen bg-gray-900">
                <Helmet>
                  <title>Dhiraj T H | AI Engineer, Freelancer, YouTuber, Innovator</title>
                  <meta name="description" content="Portfolio website of Dhiraj T H, showcasing AI engineering projects, full-stack development, mechanical innovations, and YouTube content." />
                  <meta name="keywords" content="Dhiraj TH, AI Engineer, Freelancer, YouTuber, Innovator, Portfolio" />
                </Helmet>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <Chatbot />
              </div>
            </Router>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Main App component that provides the context
function App() {
  return (
    <PortfolioProvider>
      <SocialLinksProvider>
        <AppContent />
      </SocialLinksProvider>
    </PortfolioProvider>
  );
}

export default App;
